import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import OutlineButton from 'components/OutlineButton';
import PrivateRoute from 'components/Navigation/ProtectedRoute';
import { showErrorToast, showSuccessToast } from 'components/Toast';
import RangeModalContent from './ActionModalContent';
import SolidButton from 'components/SolidButton';
import Table from 'components/Table';
import Modal from 'components/Modal';

import { ISpecialService } from 'utils/interfaces';
import { RANGE_KINDS } from 'utils/constants';
import { BUTTON_COLORS } from 'utils/enums';
import GeneralApi from 'utils/generalApi';
import { useAuth } from 'contexts/Auth';
import { validateFile } from 'utils/fileUtils';
import { read } from 'xlsx';
import { sheetToArray } from 'utils/functions';

export default function RangesPage() {
  const history = useNavigate();
  const auth = useAuth();

  const generalApi = useMemo(() => new GeneralApi(auth, history), [auth, history]);

  const [loading, setLoading] = useState(false);
  const [specialServices, setSpecialServices] = useState<Array<ISpecialService>>([]);

  const [isActionOpen, setIsActionOpen] = useState(false);
  const [actionData, setActionData] = useState<ISpecialService | undefined>(undefined);

  const [saveLoading, setSaveLoading] = useState(false);

  const checkFile = async (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    let isValid = validateFile(target, 'excel');
    if (!isValid || !target?.files?.[0]) {
      showErrorToast('Formato de documento no válido, debe ser un archivo .pdf');
      return;
    }

    let f = target.files[0],
      reader = new FileReader();
    reader.onload = function (e: ProgressEvent<FileReader>) {
      let data = e?.target?.result;
      let workbook = read(data, { type: 'array' });
      let sheet = sheetToArray(workbook.Sheets['Hoja1']);
      let newData = [...specialServices];
      for (let i = 1; i < sheet.length; i++) {
        if (sheet[i][0]) {
          let error;
          if (sheet[i][0] === '') error = 'Documento vacío';
          if (isNaN(sheet[i][0])) error = 'Documento no válido';
          if (sheet[i][0].length < 8 || sheet[i][0].length > 11)
            error = 'Documento: 8 - 11 dígitos';
          if (sheet[i][1] !== 'persona' && sheet[i][1] !== 'empresa')
            error = 'Corregir tipo de usuario';
          if (sheet[i][2] === '' || isNaN(sheet[i][2]) || sheet[i][3] === '' || isNaN(sheet[i][3]))
            error = 'Número no válido';
          let index = specialServices.findIndex((obj) => obj.number === sheet[i][0]);
          if (index > -1) {
            newData[index].number = sheet[i][0];
            newData[index].user_type =
              sheet[i][1] === 'empresa'
                ? 'company'
                : sheet[i][1] === 'persona'
                ? 'person'
                : 'error';
            newData[index].sp_compra = sheet[i][2];
            newData[index].sp_venta = sheet[i][3];
            newData[index].send = !error;
            newData[index].status = error ? error : 'A enviar';
            newData[index].action = 'edit';
          } else {
            newData.push({
              name: '-',
              number: sheet[i][0],
              user_type:
                sheet[i][1] === 'empresa'
                  ? 'company'
                  : sheet[i][1] === 'persona'
                  ? 'person'
                  : 'error',
              sp_compra: sheet[i][2],
              sp_venta: sheet[i][3],
              send: !error,
              status: error ? error : 'A enviar',
              action: 'create',
            });
          }
        }
      }
      setSpecialServices([...newData]);
    };
    reader.readAsArrayBuffer(f);
  };

  const submitAction = async () => {
    setSaveLoading(true);
    for (let i = 0; i < specialServices.length; i++) {
      const service = specialServices[i];
      if (!service.send) {
        if (i === specialServices.length - 1)
          return showSuccessToast('Tarifas actualizadas, revise la tabla para ver el resultado');
        continue;
      }

      let result: any = await (service.action === 'delete'
        ? generalApi.delete(`/special_service/${service.number}`)
        : generalApi.post('/special_service', {
            number: service.number,
            user_type: service.user_type,
            sp_compra: service.sp_compra,
            sp_venta: service.sp_venta,
          }));

      if (!result.success) {
        service.send = false;
        service.status = `Error - ${result.data.err}`;
        setSpecialServices([...specialServices]);
        continue;
      }

      service.send = false;
      service.status = service.action === 'delete' ? 'Eliminado' : 'Guardado';
      setSpecialServices([...specialServices]);
    }
    setSaveLoading(false);
    showSuccessToast('Tarifas actualizadas, revise la tabla para ver el resultado');
  };

  useEffect(() => {
    const getSpecialServices = async () => {
      setLoading(true);
      const result = await generalApi.get('/special_services');
      if (!result.success) {
        showErrorToast(result.message);
        setLoading(false);
        return;
      }
      setSpecialServices(result.data);
      setLoading(false);
    };
    getSpecialServices();
  }, [generalApi]);

  return (
    <PrivateRoute>
      <div className="text-[#1B205A]">
        <Modal
          isOpen={isActionOpen}
          onClose={() => {
            setIsActionOpen(false);
            setActionData(undefined);
          }}
        >
          <RangeModalContent
            title={actionData ? 'Editar tarifa' : 'Crear tarifa'}
            editData={actionData}
            onClose={() => {
              setIsActionOpen(false);
              setActionData(undefined);
            }}
            onSubmit={(data) => {
              const index = specialServices.findIndex(
                (service) => service.number === actionData?.number
              );
              if (index > -1) {
                specialServices[index] = {
                  ...specialServices[index],
                  ...data,
                  status: 'A enviar',
                  action: 'create',
                  send: true,
                };
              } else {
                specialServices.push({ ...data, status: 'A enviar', action: 'create', send: true });
              }
              setSpecialServices([...specialServices]);
              setIsActionOpen(false);
              setActionData(undefined);
            }}
          />
        </Modal>
        <div className="pb-8 2xl:px-24">
          <div className="mt-6 justify-between space-y-4 px-8 pb-4 sm:flex sm:space-y-0">
            <p className="text-2xl font-bold">Tarifas</p>
          </div>
          <div className="mb-4 justify-end space-y-2 sm:ml-20 sm:flex sm:space-y-0 sm:space-x-4">
            <a
              download
              target="_blank"
              rel="noreferrer"
              href="https://mindtec-exchange.s3.us-west-1.amazonaws.com/formato_tarifas.xlsx"
            >
              <OutlineButton color={BUTTON_COLORS.BLUE}>Descargar formato</OutlineButton>
            </a>
            <label
              htmlFor="inp_document_file"
              className="inline-flex w-auto cursor-pointer justify-center rounded-md border border-transparent bg-blue-700 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-800 focus:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 sm:text-sm"
            >
              <span>Importar archivo excel</span>
              <input
                id="inp_document_file"
                name="inp_document_file"
                type="file"
                className="sr-only"
                accept=".xls, .xlsx, .xlsm, .xlsb"
                onChange={checkFile}
              />
            </label>
            <SolidButton
              className="w-auto"
              color={BUTTON_COLORS.BLUE}
              onClick={() => setIsActionOpen(true)}
            >
              Nueva Tarifa
            </SolidButton>
            <SolidButton
              className="w-auto"
              color={BUTTON_COLORS.BLUE}
              isLoading={saveLoading}
              onClick={() => submitAction()}
            >
              Enviar tarifas
            </SolidButton>
          </div>
          <Table
            isLoading={loading}
            loadingText="Buscando tarifas"
            columns={[
              {
                title: 'Nombre',
                dataKey: 'name',
              },
              {
                title: 'Tipo',
                dataKey: 'user_type',
                render: (item: ISpecialService) => RANGE_KINDS[item.user_type],
              },
              {
                title: 'Documento',
                dataKey: 'number',
              },
              {
                title: 'spCompra',
                dataKey: 'sp_compra',
              },
              {
                title: 'spVenta',
                dataKey: 'sp_venta',
              },
              {
                title: 'Estado',
                dataKey: 'status',
                defaultValue: 'Guardado',
              },
              {
                title: 'Opciones',
                dataKey: 'options',
                disableSort: true,
                render: (item: ISpecialService) => (
                  <div className="flex space-x-2 pr-2">
                    <SolidButton
                      color={BUTTON_COLORS.BLUE}
                      onClick={() => {
                        setIsActionOpen(true);
                        setActionData(item);
                      }}
                    >
                      Editar
                    </SolidButton>
                    <SolidButton
                      color={BUTTON_COLORS.RED}
                      onClick={() => {
                        const index = specialServices.findIndex(
                          (service) => service.number === item.number
                        );
                        if (index === -1) return;
                        specialServices[index] = {
                          ...specialServices[index],
                          status: 'A eliminar',
                          action: 'delete',
                          send: true,
                        };

                        setSpecialServices([...specialServices]);
                      }}
                    >
                      Eliminar
                    </SolidButton>
                  </div>
                ),
              },
            ]}
            data={specialServices}
          />
        </div>
      </div>
    </PrivateRoute>
  );
}
